import { useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Badge as BadgeIcon, Email, Save } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { PageHeader } from '../../components/PageHeader';
import { useAppDispatch, useAppSelector } from '../../app/store';
import { reloadSessionData } from '../../app/sessionSlice';
import { platformApi } from '../../core/api/client';
import { fullName, initials } from '../../core/utils/format';
import { useCan } from '../../core/authorization/PermissionProvider';

export function ProfilePage({ embedded = false }: { embedded?: boolean }) {
  const canUpdate = useCan('platform.profile', 'update');
  const dispatch = useAppDispatch();
  const profile = useAppSelector((s) => s.session.profile);

  const [firstName, setFirstName] = useState(profile.firstName);
  const [lastName, setLastName] = useState(profile.lastName);
  const [phone, setPhone] = useState(profile.phone ?? '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!canUpdate) return;
    setSaving(true);
    try {
      await platformApi.updateProfile({ firstName: firstName.trim(), lastName: lastName.trim(), phone: phone.trim() || null });
      await dispatch(reloadSessionData()).unwrap();
      toast.success('Profile updated.');
    } catch {
      // Error toast is raised by the HTTP layer.
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <PageHeader level={embedded ? 2 : 1} title="My profile" subtitle="Your Platform identity across all products" />

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
          <Avatar sx={{ width: { xs: 56, sm: 72 }, height: { xs: 56, sm: 72 }, fontSize: { xs: 22, sm: 30 }, bgcolor: 'primary.main' }}>
            {initials(profile.firstName, profile.lastName)}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: '1 1 220px' }}>
            <Typography variant="h6">{fullName(profile.firstName, profile.lastName)}</Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 0.5, alignItems: 'center' }}>
              <Email fontSize="small" color="disabled" />
              <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                {profile.email}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
              <Chip size="small" icon={<BadgeIcon />} label={`@${profile.userName}`} variant="outlined" />
              {profile.emailVerifiedAt ? (
                <Chip size="small" color="success" label="Email verified" variant="outlined" />
              ) : (
                <Chip size="small" color="warning" label="Email not verified" variant="outlined" />
              )}
            </Stack>
          </Box>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            Edit details
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Changing your name updates how you appear in the feed and across products.
          </Typography>
          <Box component="form" onSubmit={(e) => { e.preventDefault(); void handleSave(); }}>
            <Stack spacing={2} maxWidth={520}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField label="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)} required fullWidth />
                <TextField label="Last name" value={lastName} onChange={(e) => setLastName(e.target.value)} required fullWidth />
              </Stack>
              <TextField label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} fullWidth />
              <Alert severity="info" variant="outlined">
                Email and username cannot be changed here yet. Password reset and email change flows are pending PlatformAPI activation/reset delivery.
              </Alert>
              <Box>
                <Button type="submit" variant="contained" startIcon={<Save />} disabled={saving || !canUpdate} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                  {saving ? 'Saving…' : 'Save changes'}
                </Button>
              </Box>
            </Stack>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
